import { css, cx } from "@emotion/css";
import { space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import { useIsLargerThanIpadResolution } from "../../hooks";
import { useSetPageTitlePrimarySegmentEffect } from "../../routing";
import { Badge } from "../Badge";
import type { BreadcrumbItem } from "../Breadcrumbs";
import { Breadcrumbs } from "../Breadcrumbs";
import type { SimpleMenuItem } from "../MenuItems";
import type { PageTitleBadge } from "../Page";
import type { PageAction, PrimaryPageAction } from "../PageActions";
import { PageActions } from "../PageActions";
export interface Level1PageHeaderProps {
    /**
     * The breadcrumbs showing at the top of primary header.
     *
     * The breadcrumbs is only visible when this property is defined.
     */
    breadcrumbs?: BreadcrumbItem[];
    /** The title of the Level1PageHeader. */
    title: string;
    /**
     * An optional primary action to display on the right side of the header
     */
    primaryAction?: PrimaryPageAction;
    /**
     * An optional list of actions to display on the right side of the header
     */
    actions?: PageAction[];
    /**
     * The badge to show on the right side of the title.
     */
    titleBadge?: PageTitleBadge;
    /**
     * An optional list of overflow items that forms an overflow menu on the right side of the header
     */
    overflowActions?: SimpleMenuItem[];
}
export function Level1PageHeader({ breadcrumbs, title, titleBadge, primaryAction, actions, overflowActions }: Level1PageHeaderProps) {
    const isLargerThanIpad = useIsLargerThanIpadResolution();
    useSetPageTitlePrimarySegmentEffect(title);
    return (
    // adora-unmask is temporary for Adora trial - #team-growth-experience-requests-and-discussion
    <header className={cx(primaryHeaderStyles, "adora-unmask")}>
            <div className={breadcrumbContainerStyles}>{breadcrumbs && <Breadcrumbs items={breadcrumbs} showBackIcon={true}/>}</div>
            <div className={cx(headerContentStyles, { [headerContentMobileStyles]: !isLargerThanIpad })}>
                <div className={titleContainerStyles}>
                    <h1 title={title} className={titleStyles}>
                        {title}
                    </h1>
                    {titleBadge && <Badge size={"medium"} variant={titleBadge.variant} icon={titleBadge.icon} label={titleBadge.label}/>}
                </div>
                <PageActions primaryAction={primaryAction} actions={actions} overflowActions={overflowActions}/>
            </div>
        </header>);
}
const primaryHeaderStyles = css({
    display: "flex",
    flexDirection: "column",
    paddingBottom: space[32],
    gap: "unset",
});
const breadcrumbContainerStyles = css({
    display: "flex",
    alignItems: "flex-start",
    height: "1.75rem",
});
const headerContentStyles = css({
    display: "flex",
    justifyContent: "space-between",
    gap: space[48],
    // This header content should have a min height set to the height of the buttons.
    // This prevents the header from changing size to accommodate buttons that are added or removed.
    minHeight: "2.25rem",
});
const headerContentMobileStyles = css({
    flexDirection: "column",
    gap: space[24],
    alignItems: "unset",
});
const titleContainerStyles = css({
    flex: "1",
    display: "flex",
    gap: space[12],
    alignItems: "center",
    overflow: "hidden",
});
const titleStyles = css({
    all: "unset",
    font: text.heading.large,
    color: themeTokens.color.text.primary,
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
});
